import { useMemo, useSyncExternalStore } from "react";
import { useNavigate, type NavigateOptions } from "react-router-dom";
import { Provider } from "./Components/Common/spectrumCompat";

type Props = { children: React.ReactNode };

function subscribePreferredColorScheme(onStoreChange: () => void): () => void {
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  mq.addEventListener("change", onStoreChange);
  return () => mq.removeEventListener("change", onStoreChange);
}

function getPreferredColorScheme(): "light" | "dark" {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function getServerPreferredColorScheme(): "light" | "dark" {
  return "light";
}

/**
 * Spectrum S2 Provider with React Router client navigation for Link / LinkButton.
 * Must render inside {@link BrowserRouter} (or equivalent).
 * `colorScheme` follows `prefers-color-scheme` (same idea as manage-profile SCSS).
 *
 * @see https://react-spectrum.adobe.com/getting-started
 */
export function SpectrumProviderWithRouter({ children }: Props) {
  const navigate = useNavigate();
  const colorScheme = useSyncExternalStore(
    subscribePreferredColorScheme,
    getPreferredColorScheme,
    getServerPreferredColorScheme,
  );
  const router = useMemo(
    () => ({
      navigate: (to: string, options?: NavigateOptions) => {
        navigate(to, options ?? {});
      },
    }),
    [navigate],
  );

  return (
    <Provider locale="en-US" colorScheme={colorScheme} router={router}>
      {children}
    </Provider>
  );
}
